// src/common/Fields/CustomSelect.jsx
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const CustomSelect = ({
    label,
    placeholder = '',
    options = [],
    value,
    onChange,
    disabled = false,
    className = '',
}) => {
    const uid = useId();
    const btnRef = useRef(null);
    const listRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const map = useMemo(() => {
        const idx = options.findIndex(o => String(o.value) === String(value));
        return { index: idx, item: idx >= 0 ? options[idx] : null };
    }, [options, value]);

    // відкрити/закрити
    const toggle = () => !disabled && setOpen(o => !o);
    const close = () => setOpen(false);

    // Клац поза елементом — закриваємо
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (!btnRef.current) return;
            const pop = listRef.current?.parentElement; // контейнер поповера
            if (!btnRef.current.contains(e.target) && !(pop && pop.contains(e.target))) {
                setOpen(false);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [open]);

    // Коли відкрили — активним робимо поточне значення, скролимо до нього
    useEffect(() => {
        if (!open) return;
        const idx = map.index >= 0 ? map.index : 0;
        setActiveIndex(idx);

        // Прокрутити список так, щоб активний був видимий
        requestAnimationFrame(() => {
            const listEl = listRef.current;
            if (!listEl) return;
            const el = listEl.querySelector(`[data-index="${idx}"]`);
            if (!el) return;
            const { offsetTop, offsetHeight } = el;
            const top = offsetTop;
            const bottom = offsetTop + offsetHeight;
            if (top < listEl.scrollTop) {
                listEl.scrollTop = top - 6;
            } else if (bottom > listEl.scrollTop + listEl.clientHeight) {
                listEl.scrollTop = bottom - listEl.clientHeight + 6;
            }
        });
    }, [open, map.index]);

    // Клавіатура
    const onKeyDown = (e) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
            return;
        }
        if (!open) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            close();
            return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prev) => {
                const dir = e.key === 'ArrowDown' ? 1 : -1;
                let next = prev;
                for (let i = 0; i < options.length; i++) {
                    next = (next + dir + options.length) % options.length;
                    if (!options[next]?.disabled) break;
                }
                // прокрутка до елемента
                requestAnimationFrame(() => {
                    const listEl = listRef.current;
                    if (!listEl) return;
                    const el = listEl.querySelector(`[data-index="${next}"]`);
                    if (!el) return;
                    const { offsetTop, offsetHeight } = el;
                    const top = offsetTop;
                    const bottom = offsetTop + offsetHeight;
                    if (top < listEl.scrollTop) {
                        listEl.scrollTop = top - 6;
                    } else if (bottom > listEl.scrollTop + listEl.clientHeight) {
                        listEl.scrollTop = bottom - listEl.clientHeight + 6;
                    }
                });
                return next;
            });
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            const opt = options[activeIndex];
            if (opt && !opt.disabled) {
                onChange?.(opt.value);
                close();
            }
        }
    };

    const handlePick = (idx) => {
        const opt = options[idx];
        if (!opt || opt.disabled) return;
        onChange?.(opt.value);
        close();
    };

    const hasValue = map.item != null;

    return (
        <div className={`cselect ${disabled ? 'is-disabled' : ''} ${className || ''}`}>
            <button
                ref={btnRef}
                id={`${uid}-btn`}
                type="button"
                className={`cselect__btn ${hasValue ? 'has-value' : ''}`}
                aria-expanded={open}
                aria-haspopup="listbox"
                onClick={toggle}
                onKeyDown={onKeyDown}
                disabled={disabled}
            >
                <span className="cselect__label">
                    {hasValue ? map.item.label : (placeholder || label || '—')}
                </span>
                <i aria-hidden className="cselect__icon" />
            </button>

            {open && (
                <div className="cselect__popover">
                    <div
                        ref={listRef}
                        className="cselect__list"
                        role="listbox"
                        aria-labelledby={`${uid}-btn`}
                        tabIndex={-1}
                    >
                        {options.map((opt, idx) => (
                            <div
                                key={String(opt.value)}
                                data-index={idx}
                                role="option"
                                aria-selected={String(value) === String(opt.value)}
                                className={[
                                    'cselect__option',
                                    idx === activeIndex ? 'is-active' : '',
                                    String(value) === String(opt.value) ? 'is-selected' : '',
                                    opt.disabled ? 'is-disabled' : '',
                                ].filter(Boolean).join(' ')}
                                onMouseEnter={() => !opt.disabled && setActiveIndex(idx)}
                                onClick={() => handlePick(idx)}
                            >
                                <span>{opt.label}</span>
                                {String(value) === String(opt.value) && <span className="cselect__check">✓</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

CustomSelect.propTypes = {
    label: PropTypes.string,
    placeholder: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
        disabled: PropTypes.bool,
    })).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    className: PropTypes.string,
};

export default CustomSelect;